/**
 * The ver-2 book reader receives page content in two shapes:
 *  - legacy: pages wrapped in an enclosing `<page>` element (with a `<page-number>` label inside),
 *  - flat: a stream of content where every page starts with an empty marker element carrying the page number
 *    (`<anchor pageNumber="N"></anchor>`, or `<t class="... anchor" pageNumber="N"></t>` in the minified mode).
 *
 * This class turns the flat markers into the `<page-number>` labels of the legacy shape, so styling and screenful
 * breakpoints work on both. Legacy content is left untouched.
 */
export class BookPageMarkersUtils {
  public static readonly PAGE_NUMBER_TAG = 'page-number';
  private static readonly MARKER_CLASS = 'anchor';
  private static readonly PAGE_NUMBER_ATTRIBUTE = 'pagenumber'; // the HTML parser lowercases attribute names

  /**
   * Replaces every page marker under [root] by a `<page-number>` element holding the page number.
   * Markers without a page number (e.g. named anchors) are left alone. Returns the number of replaced markers.
   */
  public convertMarkers(root: Element): number {
    const markers = Array.from(root.querySelectorAll('anchor, t[class]'))
      .filter(element => this.isPageMarker(element));
    markers.forEach(marker => marker.replaceWith(this.createPageNumber(marker)));
    return markers.length;
  }

  /** Whether the content is page-based: it has an enclosing `<page>` or page-number labels (converted markers). */
  public hasPages(root: Element): boolean {
    return root.querySelector('page, ' + BookPageMarkersUtils.PAGE_NUMBER_TAG) != null;
  }

  private isPageMarker(element: Element): boolean {
    const pageNumber = element.getAttribute(BookPageMarkersUtils.PAGE_NUMBER_ATTRIBUTE)?.trim();
    if (!pageNumber) return false;
    if (element.localName === BookPageMarkersUtils.MARKER_CLASS) return true;
    return element.localName === 't' && this.classTokens(element).includes(BookPageMarkersUtils.MARKER_CLASS);
  }

  // The minified mode may deliver the class attribute either as plain tokens or Base64-encoded.
  private classTokens(element: Element): string[] {
    const raw = element.getAttribute('class') ?? '';
    const plain = raw.split(/\s+/).filter(token => token.length > 0);
    if (plain.includes(BookPageMarkersUtils.MARKER_CLASS)) return plain;
    try {
      return atob(raw.trim()).split(/\s+/).filter(token => token.length > 0);
    } catch {
      return plain;
    }
  }

  private createPageNumber(marker: Element): Element {
    const label = marker.ownerDocument.createElement(BookPageMarkersUtils.PAGE_NUMBER_TAG);
    label.classList.add(BookPageMarkersUtils.PAGE_NUMBER_TAG);
    label.textContent = marker.getAttribute(BookPageMarkersUtils.PAGE_NUMBER_ATTRIBUTE)!.trim();
    return label;
  }
}
